import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { MarketplaceDeliverableType, MarketplaceOrderDeliverable, MarketplaceOrderListItemDto } from '@foodfluence/shared';
import {
  deliverableIsCarousel,
  deliverableLabel,
  MARKETPLACE_DELIVERABLE_TYPE_ORDER,
  MAX_MARKETPLACE_COUNTERS,
} from '@foodfluence/shared';
import { lcApi } from '../../../services/api/lc';
import { useMarketplaceOrders } from '../../../contexts/MarketplaceOrdersContext';
import { MarketplaceOrderStickyHeader } from '../components/MarketplaceOrderStickyHeader';
import { ff } from '../../../theme';
import { DisplayHeading, MoneyFigure, StickyCtaBar } from '../../../components/ui';

type EditableLine = {
  id: string;
  type: MarketplaceDeliverableType;
  quantity: number;
  carouselPhotos: number;
};

function newLineId() {
  return `cl_${Math.random().toString(36).slice(2, 9)}`;
}

function toEditableLines(deliverables: Record<string, MarketplaceOrderDeliverable> | undefined): EditableLine[] {
  if (!deliverables || Object.keys(deliverables).length === 0) {
    return [{ id: newLineId(), type: 'instagram_reel', quantity: 1, carouselPhotos: 5 }];
  }
  // Group by type (and by carouselPhotos for carousel types, since different counts are distinct rows).
  // For non-carousel: count field = quantity; each separate entry adds 1 to the group's quantity.
  // For carousel: count field = photos per carousel; each separate entry = 1 carousel unit.
  const grouped = new Map<string, EditableLine>();
  for (const d of Object.values(deliverables)) {
    const isCarousel = deliverableIsCarousel(d.type);
    const carouselPhotos = isCarousel ? (d.count ?? 5) : 5;
    const groupKey = isCarousel ? `${d.type}:${carouselPhotos}` : d.type;
    const existing = grouped.get(groupKey);
    if (existing) {
      existing.quantity += isCarousel ? 1 : (d.count ?? 1);
    } else {
      grouped.set(groupKey, {
        id: newLineId(),
        type: d.type,
        quantity: isCarousel ? 1 : (d.count ?? 1),
        carouselPhotos,
      });
    }
  }
  return [...grouped.values()];
}

function linesToDeliverables(lines: EditableLine[]): Record<string, MarketplaceOrderDeliverable> {
  const out: Record<string, MarketplaceOrderDeliverable> = {};
  for (const row of lines) {
    const quantity = Math.max(1, row.quantity);
    const isCarousel = deliverableIsCarousel(row.type);
    const slideCount = isCarousel ? Math.max(2, row.carouselPhotos) : undefined;
    for (let i = 0; i < quantity; i++) {
      const key = `${row.type}_${Math.random().toString(36).slice(2, 8)}`;
      out[key] = {
        id: key,
        type: row.type,
        ...(isCarousel ? { count: slideCount } : {}),
      };
    }
  }
  return out;
}

const sectionCardStyles = {
  borderRadius: '20px',
  border: '1px solid',
  borderColor: ff.line,
  bg: 'white',
  p: 4,
};

const fieldLabelStyles = { fontSize: '14px', fontWeight: 600, color: ff.ink, mb: '6px' };


export default function CounterMarketplaceOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { refetch: refetchMarketplaceOrders } = useMarketplaceOrders();

  const order = (location.state as { order?: MarketplaceOrderListItemDto })?.order;
  const brandName = order?.brandName?.trim() || 'Brand';
  const avatarLetter = (brandName[0] ?? 'B').toUpperCase();
  const isUpdate = order?.status === 'creatorCountered';

  const creatorCountersDone = useMemo(
    () => (order?.counterLogs ?? []).filter((l) => l.by === 'creator' && l.action === 'counter').length,
    [order?.counterLogs],
  );

  const initialCash: number | '' = isUpdate ? (order?.creatorProposedCash ?? '') : (order?.cash ?? 0);
  const initialDeliverables = isUpdate ? order?.creatorProposedDeliverables : order?.deliverables;

  const [cash, setCash] = useState<number | ''>(initialCash);
  const [reason, setReason] = useState(isUpdate ? (order?.creatorCounterReason ?? '') : '');
  const [rows, setRows] = useState<EditableLine[]>(() => toEditableLines(initialDeliverables));
  const [submitting, setSubmitting] = useState(false);

  const updateRow = (id: string, patch: Partial<EditableLine>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        if (patch.type !== undefined && !deliverableIsCarousel(patch.type)) {
          return next;
        }
        return next;
      })
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: newLineId(), type: 'instagram_reel', quantity: 1, carouselPhotos: 5 },
    ]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const canSubmit = typeof cash === 'number' && cash > 0 && rows.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!orderId || typeof cash !== 'number') return;
    setSubmitting(true);
    try {
      const proposedDeliverables = rows.length > 0 ? linesToDeliverables(rows) : undefined;
      const body = { proposedCash: cash, proposedDeliverables, reason: reason.trim() || undefined };
      if (isUpdate) {
        await lcApi.updateMarketplaceCounter(orderId, body);
      } else {
        await lcApi.counterMarketplaceOrder(orderId, body);
      }
      void refetchMarketplaceOrders();
      toast({ title: isUpdate ? 'Counter offer updated' : 'Counter offer submitted', status: 'success', duration: 2500, isClosable: true });
      navigate(-1);
    } catch {
      toast({ title: 'Could not submit counter offer', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box bg="white" minH="100svh" position="relative">
      <MarketplaceOrderStickyHeader
        onBack={() => navigate(-1)}
        avatarLetter={avatarLetter}
        brandName={brandName}
        instagramHandle={order?.brandInstagramHandle}
        showSupport
      />

      <Box pb="112px" px={4} pt={5}>
        <VStack spacing={4} align="stretch">
          {/* Title + counter availability */}
          <Box>
            <DisplayHeading size="26px">{isUpdate ? 'Update counter offer' : 'Make a counter offer'}</DisplayHeading>
            <Text fontSize="14px" color={ff.text2} mt={1}>
              {isUpdate
                ? 'Update your pending counter before the brand responds.'
                : `You have ${MAX_MARKETPLACE_COUNTERS - creatorCountersDone} counter offer${MAX_MARKETPLACE_COUNTERS - creatorCountersDone !== 1 ? 's' : ''} available.`}
            </Text>
          </Box>

          {/* Current brand offer summary — only show when creating (not updating) */}
          {!isUpdate && order && (
            <Box {...sectionCardStyles} bg={ff.grey}>
              <Text textStyle="eyebrow" color={ff.text2} mb={3}>
                Brand's current offer
              </Text>
              <MoneyFigure value={`$${order.cash}`} label="Total" />
            </Box>
          )}

          {/* Counter log history */}
          {(order?.counterLogs ?? []).length > 0 && (
            <Box {...sectionCardStyles}>
              <Text textStyle="eyebrow" color={ff.text2} mb={3}>
                Negotiation history
              </Text>
              <VStack spacing={2} align="stretch">
                {(order?.counterLogs ?? []).map((log, i) => (
                  <Box key={i} borderRadius="14px" border="1px solid" borderColor={ff.line} p={3} bg={ff.grey}>
                    <Flex justify="space-between" align="center" mb={1}>
                      <Text fontSize="13px" fontWeight="600" color={ff.ink}>
                        {log.by === 'creator' ? 'You' : 'Brand'} · {log.action === 'update' ? 'updated' : 'countered'}
                      </Text>
                      <Text fontSize="12px" color={ff.muted}>
                        {new Date((log.at as any).seconds * 1000).toLocaleDateString()}
                      </Text>
                    </Flex>
                    <Text textStyle="display" fontSize="18px" color={ff.ink}>${log.cash}</Text>
                    {log.reason && <Text fontSize="13px" color={ff.text2} mt={1}>{log.reason}</Text>}
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* Deliverables */}
          <Box {...sectionCardStyles}>
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>
              Deliverables
            </Text>
            <VStack spacing={3} align="stretch">
              {rows.map((row, idx) => (
                <Box
                  key={row.id}
                  borderRadius="14px"
                  border="1px solid"
                  borderColor={ff.line}
                  p={3}
                  bg={ff.grey}
                >
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="13px" fontWeight="500" color={ff.text2}>Line {idx + 1}</Text>
                    {rows.length > 1 && (
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        minW="44px"
                        h="44px"
                        variant="ghost"
                        aria-label="Remove"
                        color={ff.text2}
                        onClick={() => removeRow(row.id)}
                      />
                    )}
                  </Flex>

                  <Text {...fieldLabelStyles}>Type</Text>
                  <Select
                    mb={3}
                    bg="white"
                    value={row.type}
                    onChange={(e) => updateRow(row.id, { type: e.target.value as MarketplaceDeliverableType })}
                  >
                    {(MARKETPLACE_DELIVERABLE_TYPE_ORDER as readonly MarketplaceDeliverableType[]).map((t) => (
                      <option key={t} value={t}>{deliverableLabel(t)}</option>
                    ))}
                  </Select>

                  {deliverableIsCarousel(row.type) ? (
                    <>
                      <Text {...fieldLabelStyles}>Carousels</Text>
                      <Input
                        bg="white"
                        type="number"
                        min={1}
                        value={row.quantity}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (!isNaN(n) && n >= 1) updateRow(row.id, { quantity: n });
                        }}
                        mb={3}
                      />
                      <Text {...fieldLabelStyles}>Photos per carousel</Text>
                      <Input
                        bg="white"
                        type="number"
                        min={2}
                        value={row.carouselPhotos}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (!isNaN(n) && n >= 2) updateRow(row.id, { carouselPhotos: n });
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Text {...fieldLabelStyles}>Quantity</Text>
                      <Input
                        bg="white"
                        type="number"
                        min={1}
                        value={row.quantity}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (!isNaN(n) && n >= 1) updateRow(row.id, { quantity: n });
                        }}
                      />
                    </>
                  )}
                </Box>
              ))}
            </VStack>

            <Button
              variant="inkOutline"
              mt={3}
              w="100%"
              h="48px"
              borderStyle="dashed"
              fontSize="14px"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={addRow}
            >
              Add deliverable
            </Button>
          </Box>

          {/* Cash */}
          <Box {...sectionCardStyles}>
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>
              Your proposed price
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color={ff.text2} fontSize="15px">
                $
              </InputLeftElement>
              <Input
                type="number"
                min={1}
                value={cash === '' ? '' : cash}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setCash(isNaN(v) ? '' : v);
                }}
                fontWeight="600"
                placeholder="0"
              />
            </InputGroup>
            <Text fontSize="13px" color={ff.text2} mt={2}>Paid after content is verified</Text>
          </Box>

          {/* Reason */}
          <Box {...sectionCardStyles}>
            <Text textStyle="eyebrow" color={ff.text2} mb={3}>
              Note{' '}
              <Text as="span" fontWeight="400" textTransform="none" letterSpacing="normal">
                (optional)
              </Text>
            </Text>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain your counter offer..."
              rows={3}
              resize="none"
            />
          </Box>
        </VStack>
      </Box>

      <StickyCtaBar noNav>
        <Button
          variant="lime"
          w="100%"
          isDisabled={!canSubmit}
          isLoading={submitting}
          onClick={handleSubmit}
        >
          {isUpdate ? 'Update counter offer' : 'Submit counter offer'}
        </Button>
      </StickyCtaBar>
    </Box>
  );
}
